// /about — public marketing page, rebuilt on the sitewide PublicShell template
// (FIX-E): full nav, full legal-links footer, no duplicated What's New /
// Who-it's-for content, sitewide-standard CTA. Entity name per FIX-A;
// regime taxonomy per FIX-C (Quiet / Bull / Bear / Vol).
import { Link } from "react-router-dom";
import { Brain, ShieldCheck, Target, Activity } from "lucide-react";
import { C, SectionLabel, PublicPage } from "@/components/PublicShell";

export default function AboutPage() {
  return (
    <PublicPage>
      <section className="py-14 px-5" data-testid="about-page">
        <div className="max-w-3xl mx-auto">
          <SectionLabel text="About" color={C.muted} />
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-[1.1] tracking-tight mb-5" style={{ color: C.text }}>
            A probabilistic analytics platform for options and equity positions.
          </h1>
          <p className="text-sm leading-relaxed mb-10" style={{ color: C.muted }}>
            MarkovLab grew out of a working income-options workflow — selling premium against a long-term
            equity book, hedging drawdowns, and wanting every decision measured against data instead of
            gut feel. The platform packages that workflow's math: regime classification, strategy screening,
            historical backtesting, and portfolio risk restated against your own rules.
          </p>

          <div className="grid sm:grid-cols-2 gap-4 mb-12">
            {[
              [Brain, "Hidden Markov Engine", "Daily market behavior is classified into four regimes — Quiet, Bull, Bear, and Vol — with transition probabilities and expected sojourn times, so strategy context is explicit rather than assumed."],
              [Target, "Strategy analytics", "Cash-secured puts, covered calls, and defined-risk spreads are scored against the prevailing regime, IV rank, and your own income targets — informational scoring, never a recommendation."],
              [Activity, "Backtest Lab", "Every scored setup can be replayed against history under similar regime conditions. Backtested results are hypothetical and do not guarantee future performance."],
              [ShieldCheck, "Portfolio intelligence", "VaR/CVaR scenarios, margin utilization, concentration limits, hedge coverage and rule-based position flags — all evaluated mechanically against thresholds you set in Settings."],
            ].map(([Icon, title, body]) => (
              <div key={title} className="rounded-lg p-5" style={{ background: C.card, border: `1px solid ${C.border}` }}>
                <Icon className="w-4 h-4 mb-3" style={{ color: C.accent }} />
                <h3 className="text-sm font-bold mb-1.5" style={{ color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>{title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: C.muted }}>{body}</p>
              </div>
            ))}
          </div>

          <SectionLabel text="The company" color={C.muted} />
          <div className="rounded-lg p-5 mb-12" style={{ background: C.card, border: `1px solid ${C.border}` }} data-testid="about-company-card">
            <p className="text-xs leading-relaxed" style={{ color: C.muted }}>
              MarkovLab is operated by <span style={{ color: C.text }}>Concrete Control LLC (dba MarkovLab)</span>,
              a New Mexico limited liability company, and is built by a small independent team that runs the same
              workflow the platform models. MarkovLab provides educational and informational analytics only —
              it is not a broker-dealer or investment adviser, does not know your circumstances, and never places
              or recommends orders. Questions: <a href="mailto:info@markovlab.app" className="underline" style={{ color: C.accent }}>info@markovlab.app</a>.
            </p>
          </div>

          <div className="flex items-center gap-3" data-testid="about-cta-row">
            <Link to="/login" data-testid="about-cta"
              className="h-9 px-5 inline-flex items-center text-xs font-semibold rounded-md transition-opacity hover:opacity-85"
              style={{ background: C.accent, color: "#0a0a0a" }}>
              Start free trial
            </Link>
            <Link to="/capabilities" data-testid="about-secondary-cta"
              className="h-9 px-5 inline-flex items-center text-xs font-semibold rounded-md transition-opacity hover:opacity-85"
              style={{ border: `1px solid ${C.border}`, color: C.muted }}>
              Explore capabilities
            </Link>
          </div>
        </div>
      </section>
    </PublicPage>
  );
}
